import { isNil } from 'lodash';
import { AssignmentDto, AssignmentGradeType } from '../../apis/eols-assessment-service/eols-assessment-service.dtos';
import { getGoalIdFromVtwId, getTopicIdFromVtwId } from '../../hocs/with-base-assignment-editor/with-base-assignment-editor.utilities';
import { LessonContent, LessonContentWithTopics } from './sherpath-lesson-content.models';
import { LessonSelection } from './sherpath-lesson-selection.models';
import { getInitialSelection } from './sherpath-lesson-selection.utilities';

export const WHOLE_LESSON_GOAL = 100;

export const DEFAULT_LESSON_GRADE_POINTS = 100;

export type LessonSelectionPayload = Pick<AssignmentDto, 'assignmentTopics' | 'assignmentQuestions' | 'assignmentGoals'>;

export const isWholeLessonSelection = (content: LessonContent, selection: LessonSelection): boolean => {
  return content.topics.every(topic => selection.topicVtwIds.includes(topic.vtwId))
    && content.questions.every(question => selection.questionVtwIds.includes(question.vtwId));
};

export type CuratedGradePointsOptions = {
  isLessonEditorEnabled: boolean;
  isCustomGradePointsEnabled: boolean;
  isCurated: boolean;
};

// Without grade points, the back end sums the goals, which for a curated lesson is the selected-question count.
// Not Graded never gets points from the editor, so default them. For Scored and Pass/Fail the editor sets the default itself when custom grade points are on.
export const getDefaultCuratedGradePoints = (
  assignment: Partial<AssignmentDto> | null | undefined,
  { isLessonEditorEnabled, isCustomGradePointsEnabled, isCurated }: CuratedGradePointsOptions
): Partial<AssignmentDto> => {
  if (!assignment || !isLessonEditorEnabled || !isCurated || !isNil(assignment.gradePoints)) {
    return {};
  }
  const gradeType = assignment.assignmentGradeType ?? AssignmentGradeType.NOT_GRADED;
  if (gradeType === AssignmentGradeType.NOT_GRADED) {
    return { gradePoints: DEFAULT_LESSON_GRADE_POINTS };
  }
  const isPointsGradeType = [AssignmentGradeType.SCORED, AssignmentGradeType.PASS_FAIL].includes(gradeType);
  return isPointsGradeType && !isCustomGradePointsEnabled ? { gradePoints: DEFAULT_LESSON_GRADE_POINTS } : {};
};

// A whole-lesson selection stores no questions, so stored questions mean a topic or question was left out.
export const isCuratedLessonAssignment = (assignment: Partial<AssignmentDto> | null | undefined): boolean => {
  return (assignment?.assignmentQuestions?.length ?? 0) > 0;
};

// The goal and the topic and question rows keep the identity they have on the saved assignment so the back end updates them in place.
export const buildLessonSelectionPayload = (
  content: LessonContent,
  selection: LessonSelection,
  savedAssignment: Partial<AssignmentDto>,
  lessonVtwId: string
): LessonSelectionPayload => {
  const isWholeLesson = isWholeLessonSelection(content, selection);

  const assignmentTopics = content.topics
    .filter(topic => selection.topicVtwIds.includes(topic.vtwId))
    .map(topic => ({
      id: getTopicIdFromVtwId(savedAssignment, topic.vtwId),
      text: topic.title,
      vtwId: topic.vtwId
    }));

  const selectedQuestions = content.questions.filter(question => selection.questionVtwIds.includes(question.vtwId));
  const assignmentQuestions = isWholeLesson
    ? []
    : selectedQuestions.map((question, displayOrder) => {
      const savedQuestion = savedAssignment?.assignmentQuestions?.find(({ questionVtwId }) => questionVtwId === question.vtwId);
      return savedQuestion
        ? { ...savedQuestion, displayOrder }
        : { questionVtwId: question.vtwId, displayOrder };
    });

  const savedGoal = savedAssignment?.assignmentGoals?.find(({ vtwId }) => vtwId === lessonVtwId);
  const assignmentGoals = [{
    ...savedGoal,
    id: getGoalIdFromVtwId(savedAssignment, lessonVtwId),
    goal: isWholeLesson ? WHOLE_LESSON_GOAL : selectedQuestions.length,
    vtwId: lessonVtwId,
    text: null
  }];

  return { assignmentTopics, assignmentQuestions, assignmentGoals };
};

// No stored questions means the whole lesson; otherwise the stored topics and questions, ignoring any that are no longer in the lesson.
export const getSavedSelection = (
  content: LessonContentWithTopics,
  savedAssignment: Partial<AssignmentDto>
): LessonSelection => {
  const savedQuestionVtwIds = (savedAssignment?.assignmentQuestions || []).map(({ questionVtwId }) => questionVtwId);
  if (!savedQuestionVtwIds.length) {
    return getInitialSelection(content);
  }
  const savedTopicVtwIds = (savedAssignment.assignmentTopics || []).map(({ vtwId }) => vtwId);
  return {
    topicVtwIds: content.topics.map(topic => topic.vtwId).filter(vtwId => savedTopicVtwIds.includes(vtwId)),
    questionVtwIds: content.questions.map(question => question.vtwId).filter(vtwId => savedQuestionVtwIds.includes(vtwId))
  };
};
